// frontend/src/api/adminApi.ts
import { apiRequest, API_CONFIG } from '../config/api';
import { getToken } from './tokenStore';
import type { ClusterConfig } from '../types/k8s';

const DISCOVERY_BASE = (API_CONFIG as any).discoveryServiceURL ?? '/api/k8s';

// ─── K8s Cluster Admin ────────────────────────────────────────────────────────

// Admin cluster CRUD is mounted under /api/k8s/projects/{projectId}/admin/...
// and requires a valid JWT Bearer token (same as meta-registry-service).
async function discoveryFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err.error || err.message || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Builds the project-scoped admin URL: /api/k8s/projects/{projectId}/admin{path}
function adminUrl(projectId: string, path: string): string {
  return `${DISCOVERY_BASE}/projects/${encodeURIComponent(projectId)}/admin${path}`;
}

// Health-check is not under the admin subrouter:
// /api/k8s/clusters/{id}/health  (only RequireJWT, no project scope)
function clusterUrl(path: string): string {
  return `${DISCOVERY_BASE}${path}`;
}

export interface ClusterConfigCreateRequest {
  name: string;
  display_name: string;
  description?: string;
  platform?: 'kubernetes' | 'openshift' | 'deckhouse';
  config_data: string; // base64-encoded kubeconfig
  tags?: string[];
}

export interface ClusterConfigUpdateRequest {
  display_name?: string;
  description?: string;
  platform?: 'kubernetes' | 'openshift' | 'deckhouse';
  config_data?: string;
  is_public?: boolean;
  tags?: string[];
}

export async function adminListClusters(projectId: string, params: { active?: boolean; platform?: string } = {}): Promise<ClusterConfig[]> {
  const qs = new URLSearchParams();
  if (params.active !== undefined) qs.set('active', String(params.active));
  if (params.platform) qs.set('platform', params.platform);
  const q = qs.toString() ? `?${qs}` : '';
  return discoveryFetch<ClusterConfig[]>(adminUrl(projectId, `/clusters${q}`));
}

export async function adminGetCluster(projectId: string, id: string): Promise<ClusterConfig> {
  return discoveryFetch<ClusterConfig>(adminUrl(projectId, `/clusters/${id}`));
}

export async function adminCreateCluster(projectId: string, data: ClusterConfigCreateRequest): Promise<ClusterConfig> {
  return discoveryFetch<ClusterConfig>(adminUrl(projectId, '/clusters'), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminUpdateCluster(projectId: string, id: string, data: ClusterConfigUpdateRequest): Promise<ClusterConfig> {
  return discoveryFetch<ClusterConfig>(adminUrl(projectId, `/clusters/${id}`), {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteCluster(projectId: string, id: string): Promise<void> {
  await discoveryFetch<void>(adminUrl(projectId, `/clusters/${id}`), { method: 'DELETE' });
}

export async function adminActivateCluster(projectId: string, id: string): Promise<ClusterConfig> {
  return discoveryFetch<ClusterConfig>(adminUrl(projectId, `/clusters/${id}/activate`), { method: 'POST' });
}

export async function adminDeactivateCluster(projectId: string, id: string): Promise<ClusterConfig> {
  return discoveryFetch<ClusterConfig>(adminUrl(projectId, `/clusters/${id}/deactivate`), { method: 'POST' });
}

export async function adminCheckClusterHealth(projectId: string, id: string): Promise<Record<string, unknown>> {
  return discoveryFetch(clusterUrl(`/clusters/${id}/health`));
}

export async function adminTestClusterConnection(projectId: string, id: string): Promise<Record<string, unknown>> {
  return discoveryFetch(adminUrl(projectId, `/clusters/${id}/test`), { method: 'POST' });
}

// ─── GitLab Connections (IS repository service) ───────────────────────────────

export interface GitlabConnection {
  id: string;
  name: string;
  gitlab_url: string;
  service_token?: string; // write-only: not returned by GET, accepted in POST/PUT
  project_id?: string;    // arch-editor project UUID (set automatically when using project-scoped routes)
  gitlab_project_id?: number; // GitLab numeric project ID
  branch?: string;
  poll_interval?: number;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export async function listGitlabConnections(): Promise<GitlabConnection[]> {
  return (apiRequest as any)('/admin/gitlab/connections', { method: 'GET' }) ?? [];
}

export async function createGitlabConnection(data: Partial<GitlabConnection>): Promise<GitlabConnection> {
  return (apiRequest as any)('/admin/gitlab/connections', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateGitlabConnection(id: string, data: Partial<GitlabConnection>): Promise<GitlabConnection> {
  return (apiRequest as any)(`/admin/gitlab/connections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteGitlabConnection(id: string): Promise<void> {
  await (apiRequest as any)(`/admin/gitlab/connections/${id}`, { method: 'DELETE' });
}

export async function testGitlabConnection(id: string): Promise<{ success: boolean; message: string }> {
  return (apiRequest as any)(`/admin/gitlab/connections/${id}/test`, { method: 'POST' });
}

export async function triggerGitlabPullAll(id: string): Promise<void> {
  await (apiRequest as any)(`/admin/gitlab/connections/${id}/pull`, { method: 'POST' });
}

// ─── Project-scoped GitLab Connections (project owner/admin) ─────────────────

export async function listProjectGitlabConnections(projectId: string): Promise<GitlabConnection[]> {
  return (apiRequest as any)(`/projects/${projectId}/gitlab/connections`, { method: 'GET' }) ?? [];
}

export async function createProjectGitlabConnection(projectId: string, data: Partial<GitlabConnection>): Promise<GitlabConnection> {
  return (apiRequest as any)(`/projects/${projectId}/gitlab/connections`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProjectGitlabConnection(projectId: string, id: string, data: Partial<GitlabConnection>): Promise<GitlabConnection> {
  return (apiRequest as any)(`/projects/${projectId}/gitlab/connections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProjectGitlabConnection(projectId: string, id: string): Promise<void> {
  await (apiRequest as any)(`/projects/${projectId}/gitlab/connections/${id}`, { method: 'DELETE' });
}

export async function testProjectGitlabConnection(projectId: string, id: string): Promise<{ success: boolean; message: string }> {
  return (apiRequest as any)(`/projects/${projectId}/gitlab/connections/${id}/test`, { method: 'POST' });
}

export async function triggerProjectGitlabPullAll(projectId: string, id: string): Promise<void> {
  await (apiRequest as any)(`/projects/${projectId}/gitlab/connections/${id}/pull`, { method: 'POST' });
}

// ─── Sync Conflicts ───────────────────────────────────────────────────────────

export interface SyncConflict {
  id: string;
  entry_id?: string;
  diagram_id?: string;
  gitlab_sha: string;
  platform_sha: string;
  conflict_data: Record<string, unknown>;
  created_at: string;
}

export async function listSyncConflicts(): Promise<SyncConflict[]> {
  return (apiRequest as any)('/admin/sync-conflicts', { method: 'GET' }) ?? [];
}

export async function acceptGitlabVersion(id: string): Promise<void> {
  await (apiRequest as any)(`/admin/sync-conflicts/${id}/accept-gitlab`, { method: 'POST' });
}

export async function acceptPlatformVersion(id: string): Promise<void> {
  await (apiRequest as any)(`/admin/sync-conflicts/${id}/accept-platform`, { method: 'POST' });
}
